import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { contactAPI, promotionsAPI, contactPageAPI } from '../services/api';
import { LoadingScreen, ErrorScreen } from '../hooks/useApiLoading';
import { 
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  User,
  MessageCircle,
  CheckCircle,
  AlertCircle,
  Globe,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  Calendar,
  Zap,
  HeadphonesIcon,
  Building,
  ArrowRight
} from 'lucide-react';
import { gsap } from 'gsap';
import ContactForm from '../components/Contact/ContactForm';
import { useNotificationHook } from '../hooks/useNotificationHook';
import GoogleMap from '../components/Google/GoogleMap';
import { useGoogleAnalytics } from '../hooks/useGoogleAnalytics';

const Contact = () => {
  const { notify } = useNotificationHook();
  const { trackContactAction, trackButtonClick } = useGoogleAnalytics();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    message: '',
    timeline: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const contactRef = useRef(null);

  // Banner promotions state
  const [promotions, setPromotions] = useState([]);
  const [topBannerPromotions, setTopBannerPromotions] = useState([]);
  const [topBannerIndex, setTopBannerIndex] = useState(0);
  const [error, setError] = useState(null);

  // Contact page content state
  const [contactContent, setContactContent] = useState({
    main_title: 'Get In',
    gradient_title: 'Touch',
    description: 'Ready to start your project? Let\'s discuss how we can help bring your vision to life.',
    contact_options: [
      {
        id: 1,
        type: 'call',
        icon: 'Phone',
        icon_background: 'from-green-600 to-green-700',
        title: 'Call Us',
        description: 'Speak directly with our team',
        value: '9876543210',
        link: 'tel:+919876543210'
      },
      {
        id: 2,
        type: 'email',
        icon: 'Mail',
        icon_background: 'from-blue-600 to-blue-700',
        title: 'Email Us',
        description: 'Send us a detailed message',
        value: 'info@niswainfotech.com',
        link: 'mailto:info@niswainfotech.com'
      },
      {
        id: 3,
        type: 'whatsapp',
        icon: 'MessageCircle',
        icon_background: 'from-green-500 to-green-600',
        title: 'WhatsApp',
        description: 'Quick chat on WhatsApp',
        value: '+91 9876543210',
        link: 'https://wa.me/919876543210'
      },
      {
        id: 4,
        type: 'schedule',
        icon: 'Calendar',
        icon_background: 'from-purple-600 to-purple-700',
        title: 'Schedule Meeting',
        description: 'Book a consultation call',
        value: 'Book Now',
        link: '/schedule'
      }
    ],
    form_title: 'Send Us a Message',
    form_description: 'Fill out the form below and we\'ll get back to you within 24 hours.',
    other_contact_title: 'Other Ways to Reach Us',
    phone_icon: 'Phone',
    phone_subtitle: 'Phone',
    phone_value: '9876543210',
    office_title: 'Visit Our Office',
    office_items: [
      {
        id: 1,
        type: 'address',
        icon: 'MapPin',
        title: 'Address',
        value: '123 Business District, Andheri East, Mumbai, Maharashtra 400069',
        link: ''
      },
      {
        id: 2,
        type: 'hours',
        icon: 'Clock',
        title: 'Business Hours',
        value: 'Monday - Friday: 9:00 AM - 6:00 PM\nSaturday: 10:00 AM - 4:00 PM\nSunday: Closed',
        link: ''
      },
      {
        id: 3,
        type: 'email',
        icon: 'Mail',
        title: 'Email',
        value: 'info@niswainfotech.com',
        link: 'mailto:info@niswainfotech.com'
      },
      {
        id: 4,
        type: 'phone',
        icon: 'Phone',
        title: 'Phone',
        value: '9876543210',
        link: 'tel:+919876543210'
      }
    ],
    map_title: 'Location',
    map_address: 'Mumbai, Maharashtra',
    google_maps_api_key: '',
    social_title: 'Follow Us',
    social_links: [
      { name: 'Facebook', icon: 'Facebook', url: 'https://facebook.com/niswainfotech', color: 'hover:text-blue-600' },
      { name: 'Twitter', icon: 'Twitter', url: 'https://twitter.com/niswainfotech', color: 'hover:text-blue-400' },
      { name: 'Instagram', icon: 'Instagram', url: 'https://instagram.com/niswainfotech', color: 'hover:text-pink-600' },
      { name: 'LinkedIn', icon: 'Linkedin', url: 'https://linkedin.com/company/niswainfotech', color: 'hover:text-blue-700' },
      { name: 'YouTube', icon: 'Youtube', url: 'https://youtube.com/niswainfotech', color: 'hover:text-red-600' }
    ],
    faq_title: 'Frequently Asked Questions',
    faq_description: 'Quick answers to common questions about our services and process',
    faq_items: [
      {
        question: 'How long does a typical project take?',
        answer: 'Project timelines vary based on complexity. Simple websites take 2-4 weeks, while complex applications can take 8-16 weeks. We provide detailed timelines during consultation.'
      },
      {
        question: 'Do you provide ongoing support and maintenance?',
        answer: 'Yes, we offer comprehensive support packages including regular updates, security monitoring, performance optimization, and technical support.'
      },
      {
        question: 'What is your payment structure?',
        answer: 'We typically work with a 50% upfront payment and 50% upon completion. For larger projects, we can arrange milestone-based payments.'
      },
      {
        question: 'Can you work with our existing team?',
        answer: 'Absolutely! We collaborate seamlessly with in-house teams and can integrate with your existing workflows and tools.'
      }
    ]
  });

  // CTA section state
  const [cta, setCta] = useState({ 
    cta_title: 'Ready to Start Your Project?', 
    cta_description: 'Let\'s discuss your project requirements and create something amazing together',
    cta_button1_text: 'Start Your Project',
    cta_button1_link: '/contact',
    cta_button2_text: 'Get Free Quote',
    cta_button2_link: '/quote'
  });

  // Fetch contact page content
  const fetchContactContent = async () => {
    try {
      setError(null);
      const response = await contactPageAPI.getContent();
      if (response?.data?.success) {
        const data = response.data.data;
        setContactContent({
          main_title: data.main_title || 'Get In',
          gradient_title: data.gradient_title || 'Touch',
          description: data.description || 'Ready to start your project? Let\'s discuss how we can help bring your vision to life.',
          contact_options: data.contact_options || [
            {
              id: 1,
              type: 'call',
              icon: 'Phone',
              icon_background: 'from-green-600 to-green-700',
              title: 'Call Us',
              description: 'Speak directly with our team',
              value: '9876543210',
              link: 'tel:+919876543210'
            },
            {
              id: 2,
              type: 'email',
              icon: 'Mail',
              icon_background: 'from-blue-600 to-blue-700',
              title: 'Email Us',
              description: 'Send us a detailed message',
              value: 'info@niswainfotech.com',
              link: 'mailto:info@niswainfotech.com'
            },
            {
              id: 3,
              type: 'whatsapp',
              icon: 'MessageCircle',
              icon_background: 'from-green-500 to-green-600',
              title: 'WhatsApp',
              description: 'Quick chat on WhatsApp',
              value: '+91 9876543210',
              link: 'https://wa.me/919876543210'
            },
            {
              id: 4,
              type: 'schedule',
              icon: 'Calendar',
              icon_background: 'from-purple-600 to-purple-700',
              title: 'Schedule Meeting',
              description: 'Book a consultation call',
              value: 'Book Now',
              link: '/schedule'
            }
          ],
          form_title: data.form_title || 'Send Us a Message',
          form_description: data.form_description || 'Fill out the form below and we\'ll get back to you within 24 hours.',
          other_contact_title: data.other_contact_title || 'Other Ways to Reach Us',
          other_contact_options: data.other_contact_options || [
            {
              id: 1,
              icon: 'Phone',
              title: 'Call Us',
              description: 'Give us a call',
              value: '+91 9876543210'
            },
            {
              id: 2,
              icon: 'Mail',
              title: 'Email Us',
              description: 'Drop us an email',
              value: 'info@niswainfotech.com'
            },
            {
              id: 3,
              icon: 'MessageCircle',
              title: 'WhatsApp',
              description: 'Quick chat on WhatsApp',
              value: '+91 9876543210'
            }
          ],
          phone_icon: data.phone_icon || 'Phone',
          phone_subtitle: data.phone_subtitle || 'Phone',
          phone_value: data.phone_value || '9876543210',
          office_title: data.office_title || 'Visit Our Office',
          office_items: data.office_items || [
            {
              id: 1,
              type: 'address',
              icon: 'MapPin',
              title: 'Address',
              value: '123 Business District, Andheri East, Mumbai, Maharashtra 400069',
              link: ''
            },
            {
              id: 2,
              type: 'hours',
              icon: 'Clock',
              title: 'Business Hours',
              value: 'Monday - Friday: 9:00 AM - 6:00 PM\nSaturday: 10:00 AM - 4:00 PM\nSunday: Closed',
              link: ''
            },
            {
              id: 3,
              type: 'email',
              icon: 'Mail',
              title: 'Email',
              value: 'info@niswainfotech.com',
              link: 'mailto:info@niswainfotech.com'
            },
            {
              id: 4,
              type: 'phone',
              icon: 'Phone',
              title: 'Phone',
              value: '9876543210',
              link: 'tel:+919876543210'
            }
          ],
          map_title: data.map_title || 'Location',
          map_address: data.map_address || 'Mumbai, Maharashtra',
          google_maps_api_key: data.google_maps_api_key || '',
          map_phone: data.map_phone || '+91 98765 43210',
          map_email: data.map_email || 'hello@niswainfotech.com',
          social_title: data.social_title || 'Follow Us',
          social_links: data.social_links || [
            { name: 'Facebook', icon: 'Facebook', url: 'https://facebook.com/niswainfotech', color: 'hover:text-blue-600' },
            { name: 'Twitter', icon: 'Twitter', url: 'https://twitter.com/niswainfotech', color: 'hover:text-blue-400' },
            { name: 'Instagram', icon: 'Instagram', url: 'https://instagram.com/niswainfotech', color: 'hover:text-pink-600' },
            { name: 'LinkedIn', icon: 'Linkedin', url: 'https://linkedin.com/company/niswainfotech', color: 'hover:text-blue-700' },
            { name: 'YouTube', icon: 'Youtube', url: 'https://youtube.com/niswainfotech', color: 'hover:text-red-600' }
          ],
          faq_title: data.faq_title || 'Frequently Asked Questions',
          faq_description: data.faq_description || 'Quick answers to common questions about our services and process',
          faq_items: data.faq_items || [
            {
              question: 'How long does a typical project take?',
              answer: 'Project timelines vary based on complexity. Simple websites take 2-4 weeks, while complex applications can take 8-16 weeks. We provide detailed timelines during consultation.'
            },
            {
              question: 'Do you provide ongoing support and maintenance?',
              answer: 'Yes, we offer comprehensive support packages including regular updates, security monitoring, performance optimization, and technical support.'
            },
            {
              question: 'What is your payment structure?',
              answer: 'We typically work with a 50% upfront payment and 50% upon completion. For larger projects, we can arrange milestone-based payments.'
            },
            {
              question: 'Can you work with our existing team?',
              answer: 'Absolutely! We collaborate seamlessly with in-house teams and can integrate with your existing workflows and tools.'
            }
          ]
        });
      } else {
        setError('Failed to load contact content');
      }
    } catch (err) {
      console.error('Error fetching contact content:', err);
      setError('Failed to load contact content');
    }
  };

  // Fetch CTA data
  const fetchCTAData = async () => {
    try {
      const response = await contactPageAPI.getContent();
      if (response?.data?.success) {
        const data = response.data.data;
        setCta({
          cta_title: data.cta_title || 'Ready to Start Your Project?',
          cta_description: data.cta_description || 'Let\'s discuss your project requirements and create something amazing together',
          cta_button1_text: data.cta_button1_text || 'Start Your Project',
          cta_button1_link: data.cta_button1_link || '/contact',
          cta_button2_text: data.cta_button2_text || 'Get Free Quote',
          cta_button2_link: data.cta_button2_link || '/quote'
        });
      }
    } catch (err) {
      console.error('Error fetching CTA data:', err);
      // Keep default values if API fails
    }
  };

  useEffect(() => {
    fetchContactContent(); // Fetch contact content on mount
    fetchCTAData(); // Fetch CTA data on mount
    
    // Set initial states
    gsap.set('.contact-title', { opacity: 0, y: 50 });
    gsap.set('.contact-subtitle', { opacity: 0, y: 30 });
    gsap.set('.contact-card', { opacity: 0, y: 40 });
    gsap.set('.contact-form', { opacity: 0, x: 50 });
    gsap.set('.contact-info', { opacity: 0, x: -50 });

    // Animate elements
    const tl = gsap.timeline({ delay: 0.2 });
    tl.to('.contact-title', { duration: 0.8, opacity: 1, y: 0, ease: 'power3.out' })
      .to('.contact-subtitle', { duration: 0.8, opacity: 1, y: 0, ease: 'power3.out' }, '-=0.5')
      .to('.contact-card', { duration: 0.6, opacity: 1, y: 0, stagger: 0.1, ease: 'power3.out' }, '-=0.3')
      .to('.contact-info', { duration: 0.8, opacity: 1, x: 0, ease: 'power3.out' }, '-=0.5')
      .to('.contact-form', { duration: 0.8, opacity: 1, x: 0, ease: 'power3.out' }, '-=0.6');

    return () => {
      tl.kill();
    };
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('submitting');
    trackContactAction('form_submit', 'contact_form');

    try {
      const response = await contactAPI.submit(formData);
      if (response.data.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          budget: '',
          message: '',
          timeline: ''
        });
        trackContactAction('form_success', 'contact_form');
        notify.success('Message sent successfully!', 'Success');
      } else {
        setSubmitStatus('error');
        trackContactAction('form_error', 'contact_form');
        notify.error('Failed to send message.', 'Send Error');
      }
    } catch (error) {
      setSubmitStatus('error');
      trackContactAction('form_error', 'contact_form');
      notify.error('Failed to send message. Please try again.', 'Network Error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  // Helper function to get icon component
  const getIconComponent = (iconName) => {
    const icons = {
      'Phone': Phone,
      'Mail': Mail,
      'MapPin': MapPin,
      'Clock': Clock,
      'Send': Send,
      'User': User,
      'MessageCircle': MessageCircle,
      'CheckCircle': CheckCircle,
      'AlertCircle': AlertCircle,
      'Globe': Globe,
      'Facebook': Facebook,
      'Twitter': Twitter,
      'Instagram': Instagram,
      'Linkedin': Linkedin,
      'Youtube': Youtube,
      'Calendar': Calendar,
      'Zap': Zap,
      'Headphones': HeadphonesIcon,
      'Building': Building,
      'ArrowRight': ArrowRight
    };
    return icons[iconName] || Phone;
  };

  const contactMethods = contactContent.contact_options?.map(option => ({
    icon: getIconComponent(option.icon),
    title: option.title,
    description: option.description,
    value: option.value,
    action: option.link,
    color: option.icon_background
  })) || [
    {
      icon: getIconComponent('Phone'),
      title: 'Call Us',
      description: 'Speak directly with our team',
      value: '9876543210',
      action: 'tel:+919876543210',
      color: 'from-green-600 to-green-700'
    },
    {
      icon: Mail,
      title: 'Email Us',
      description: 'Send us a detailed message',
      value: 'info@niswainfotech.com',
      action: 'mailto:info@niswainfotech.com',
      color: 'from-blue-600 to-blue-700'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      description: 'Quick chat on WhatsApp',
      value: '+91 9876543210',
      action: 'https://wa.me/919876543210',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: Calendar,
      title: 'Schedule Meeting',
      description: 'Book a consultation call',
      value: 'Book Now',
      action: '/schedule',
      color: 'from-purple-600 to-purple-700'
    }
  ];

  const officeInfo = {
    address: contactContent.address_value,
    hours: [
      { day: 'Monday - Friday', time: '9:00 AM - 6:00 PM' },
      { day: 'Saturday', time: '10:00 AM - 4:00 PM' },
      { day: 'Sunday', time: 'Closed' }
    ],
    email: 'info@niswainfotech.com',
    phone: '+91 9876543210',
    website: 'www.niswainfotech.com'
  };

  const socialLinks = contactContent.social_links || [
    { name: 'Facebook', icon: Facebook, url: 'https://facebook.com/niswainfotech', color: 'hover:text-blue-600' },
    { name: 'Twitter', icon: Twitter, url: 'https://twitter.com/niswainfotech', color: 'hover:text-blue-400' },
    { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/niswainfotech', color: 'hover:text-pink-600' },
    { name: 'LinkedIn', icon: Linkedin, url: 'https://linkedin.com/company/niswainfotech', color: 'hover:text-blue-700' },
    { name: 'YouTube', icon: Youtube, url: 'https://youtube.com/niswainfotech', color: 'hover:text-red-600' }
  ];

  const services = [
    'Web Development',
    'Mobile App Development',
    'Digital Marketing',
    'E-commerce Solutions',
    'UI/UX Design',
    'Custom Software',
    'Digital Products',
    'Consultation'
  ];

  const budgetRanges = [
    'Under ₹50,000',
    '₹50,000 - ₹1,00,000',
    '₹1,00,000 - ₹2,50,000',
    '₹2,50,000 - ₹5,00,000',
    'Above ₹5,00,000'
  ];

  const timelines = [
    'ASAP',
    '1-2 weeks',
    '1 month',
    '2-3 months',
    '3-6 months',
    '6+ months'
  ];

  // Show loading screen when data is being fetched
  if (!contactContent.main_title) {
    return <LoadingScreen message="Loading contact page..." />;
  }

  // Show error screen when there's an error
  if (error) {
    return <ErrorScreen error={error} onRetry={() => window.location.reload()} message="Unable to Load Contact Page" />;
  }

  return (
    <div className="min-h-screen">
      {/* Modern Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="contact-title text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-8">
              {contactContent.main_title} <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">{contactContent.gradient_title}</span>
            </h1>
            <p className="contact-subtitle text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-12 leading-relaxed">
              {contactContent.description}
            </p>
          </div>
        </div>
      </section>

      {/* Modern Contact Methods */}
      <section className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactMethods.map((method, index) => {
              const IconComponent = method.icon;
              return (
                <a
                  key={index}
                  href={method.action}
                  className="contact-card card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl hover:scale-105 transition-all duration-500 text-center p-8 border border-white/20"
                >
                  <div className={`w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {method.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                    {method.description}
                  </p>
                  <p className="text-blue-600 dark:text-blue-400 font-semibold">
                    {method.value}
                  </p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Modern Main Contact Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-purple-800 to-slate-800 opacity-20"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="contact-form">
              <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/20">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  {contactContent.form_title}
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-8">
                  {contactContent.form_description}
                </p>

                <ContactForm variant="full" otherContactOptions={contactContent.other_contact_options || []} />
              </div>
            </div>

            {/* Contact Information */}
            <div className="contact-info space-y-8">
              {/* Office Info */}
              <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/20">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {contactContent.office_title}
                </h3>
                
                <div className="space-y-6">
                  {contactContent.office_items?.map((item, index) => {
                    const IconComponent = getIconComponent(item.icon);
                    return (
                      <div key={item.id} className="flex items-start gap-4">
                        <IconComponent className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-gray-900 dark:text-white font-semibold mb-1">{item.title}</p>
                          {item.link ? (
                            <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                              {item.value}
                            </a>
                          ) : (
                            <p className="text-gray-600 dark:text-gray-300 whitespace-pre-line">{item.value}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Google Map */}
              <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/20">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {contactContent.map_title}
                </h3>
                <GoogleMap
                  address={contactContent.map_address || 'Mumbai, Maharashtra'}
                  apiKey={contactContent.google_maps_api_key}
                  height="400px"
                  showContactInfo={true}
                  contactInfo={{
                    phone: contactContent.map_phone || contactContent.phone_value || '9876543210',
                    email: contactContent.map_email || contactContent.call_value || 'info@niswainfotech.com'
                  }}
                  className="rounded-2xl overflow-hidden"
                />
              </div>

              {/* Social Links */}
              <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/20">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {contactContent.social_title}
                </h3>
                <div className="flex gap-4">
                  {socialLinks.map((social, index) => {
                    const IconComponent = getIconComponent(social.icon);
                    return (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-14 h-14 bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center text-gray-600 dark:text-gray-300 ${social.color} transition-all duration-300 hover:scale-110 border border-white/20 shadow-lg`}
                      >
                        <IconComponent className="w-6 h-6" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modern FAQ Section */}
      <section className="py-24 bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              {contactContent.faq_title}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {contactContent.faq_description}
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-8">
            {contactContent.faq_items.map((faq, index) => (
              <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/20 hover:scale-105 transition-all duration-500">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  {faq.question}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modern CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50 dark:from-blue-600 dark:via-purple-600 dark:to-indigo-600 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-100/20 via-purple-100/20 to-indigo-100/20 dark:from-blue-700/20 dark:via-purple-700/20 dark:to-indigo-700/20"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            {cta.cta_title}
          </h2>
          <p className="text-xl text-gray-600 dark:text-white/90 mb-12 max-w-3xl mx-auto">
            {cta.cta_description}
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link 
              to={cta.cta_button1_link} 
              className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-5 rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-500 flex items-center justify-center gap-3 shadow-glow"
            >
              {cta.cta_button1_text}
              <ArrowRight className="w-6 h-6" />
            </Link>
            <Link 
              to={cta.cta_button2_link} 
              className="card-3d border-2 border-gray-900 dark:border-white text-gray-900 dark:text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-gray-900 hover:text-white dark:hover:bg-white dark:hover:text-gray-900 transition-all duration-500 shadow-glow"
            >
              {cta.cta_button2_text}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
